import type { SkillConfig, SkillId } from '../../types';
import { isFailoverActive, getFailoverTarget } from './modelFailoverService';

// Hybrid provider mapping:
// - Gemini Flash: cheap/conversational (Intake Nurse, Client Enrichment)
// - Claude Opus 4.6: consistency-critical clinical authority (Lab Analyst, Protocol Builder, Med Reasoning)
// - Claude Sonnet: precision-critical narrative (Report Generator)
// - MedGemma: Phase 2 only (Med Reasoning will migrate)
//
// CLINICAL SAFETY: fallbackProvider is set to 'none' for all clinical agents.
// If the primary provider fails, we surface an error to the patient — we do NOT
// silently substitute mock/fabricated data. Mock data is only served when
// mockMode === true (explicit demo/test mode via VITE_ENABLE_MOCK=true).
//
// MODEL DEGRADATION FAILOVER: When accuracy drops >10% from baseline, the
// modelDegradationResponse system activates cloud failover. The provider router
// detects this via modelFailoverService and routes clinical agents to the cloud
// fallback model (gemini-2.5-pro) until accuracy recovers or manual override.
//
// PROVIDER ROUTER: fallbackChain defines ordered failover targets.
// The router tries the primary provider first, then each fallback in order.
// 'local' entries are placeholders for future on-premise MedGemma inference.

export const SKILL_CONFIGS: Record<SkillId, SkillConfig> = {
  'intake-nurse': {
    skillId: 'intake-nurse',
    name: 'Intake Nurse',
    provider: 'gemini',
    model: 'gemini-2.5-flash',
    temperature: 0.7,
    maxTokens: 2048,
    fallbackProvider: 'none',
    fallbackChain: [
      { provider: 'gemini', model: 'gemini-2.5-flash' },
      { provider: 'claude', model: 'claude-sonnet-4-5-20250929' },
    ],
  },
  'lab-analyst': {
    skillId: 'lab-analyst',
    name: 'Lab Analyst',
    provider: 'claude',
    model: 'claude-opus-4-6',
    temperature: 0,
    maxTokens: 32768,
    fallbackProvider: 'none',
    fallbackChain: [
      { provider: 'claude', model: 'claude-opus-4-6' },
      { provider: 'gemini', model: 'gemini-2.5-pro' },
      { provider: 'local', model: 'medgemma-27b' },
    ],
  },
  'protocol-builder': {
    skillId: 'protocol-builder',
    name: 'Protocol Builder',
    provider: 'claude',
    model: 'claude-opus-4-6',
    temperature: 0,
    maxTokens: 4096,
    fallbackProvider: 'none',
    fallbackChain: [
      { provider: 'claude', model: 'claude-opus-4-6' },
      { provider: 'gemini', model: 'gemini-2.5-pro' },
      { provider: 'local', model: 'medgemma-27b' },
    ],
  },
  'med-reasoning': {
    skillId: 'med-reasoning',
    name: 'Medical Reasoning',
    provider: 'claude',
    model: 'claude-opus-4-6',
    temperature: 0.2,
    maxTokens: 4096,
    fallbackProvider: 'none',
    fallbackChain: [
      { provider: 'claude', model: 'claude-opus-4-6' },
      { provider: 'gemini', model: 'gemini-2.5-pro' },
    ],
  },
  'client-enrichment': {
    skillId: 'client-enrichment',
    name: 'Client Enrichment',
    provider: 'gemini',
    model: 'gemini-2.5-flash',
    temperature: 0.5,
    maxTokens: 2048,
    fallbackProvider: 'none',
    fallbackChain: [
      { provider: 'gemini', model: 'gemini-2.5-flash' },
      { provider: 'claude', model: 'claude-sonnet-4-5-20250929' },
    ],
  },
  'report-generator': {
    skillId: 'report-generator',
    name: 'Report Generator',
    provider: 'claude',
    model: 'claude-sonnet-4-5-20250929',
    temperature: 0.3,
    maxTokens: 8192,
    fallbackProvider: 'none',
    fallbackChain: [
      { provider: 'claude', model: 'claude-sonnet-4-5-20250929' },
      { provider: 'gemini', model: 'gemini-2.5-pro' },
    ],
  },
  'qwen-coder': {
    skillId: 'qwen-coder',
    name: 'Qwen Coder',
    provider: 'qwen',
    model: 'qwen-2.5-coder-32b',
    temperature: 0,
    maxTokens: 4096,
    fallbackProvider: 'none',
    fallbackChain: [
      { provider: 'qwen', model: 'qwen-2.5-coder-32b' },
      { provider: 'claude', model: 'claude-sonnet-4-5-20250929' },
    ],
  },
};

export function getProvider(skillId: SkillId, mockMode: boolean) {
  const config = SKILL_CONFIGS[skillId];
  // CLINICAL SAFETY: mockMode is only true when VITE_ENABLE_MOCK=true (demo/test).
  // In production, mockMode is always false and we use the real provider.
  // If the real provider fails, the calling code throws an error — no silent mock fallback.
  if (mockMode) {
    return { type: 'mock' as const, config };
  }

  // MODEL DEGRADATION FAILOVER: When the accuracy monitor detects >10% drop,
  // route clinical agents to the cloud fallback model. Only affects agents whose
  // primary is claude (the monitored model). Non-clinical agents are unaffected.
  if (isFailoverActive() && config.provider === 'claude') {
    const failoverModel = getFailoverTarget();
    if (failoverModel && config.fallbackChain) {
      const cloudFallback = config.fallbackChain.find(
        (f) => f.provider !== 'local' && f.model !== config.model,
      );
      if (cloudFallback) {
        const failoverConfig: SkillConfig = {
          ...config,
          provider: cloudFallback.provider === 'gemini' ? 'gemini' : 'claude',
          model: cloudFallback.model,
        };
        return { type: failoverConfig.provider, config: failoverConfig };
      }
    }
  }

  return { type: config.provider, config };
}
